import React, { forwardRef, type ReactNode, useState } from 'react';
import styled from 'styled-components';
import EmojiPickerReact, {
  EmojiClickData,
  EmojiStyle,
  Theme,
} from 'emoji-picker-react';
import {
  autoUpdate,
  flip,
  offset,
  Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
} from '@floating-ui/react';
import { useUserSettingsSelector } from '../../../../redux/selectorHooks';
import { FloatingLayer } from '../../../FloatingManager';
import { theme } from '../../../../styles/theme';
import { IStyled } from '../../../../styles/types';

interface IProps extends IStyled {
  children: ReactNode;
  onSelect: (emojiData: EmojiClickData) => void;
  onOpen?: () => void;
  onClose?: () => void;
  placement?: Placement;
  reactionsDefaultOpen?: boolean;
}

const StyledEmojiPickerReact = styled(EmojiPickerReact)`
  && {
    border-width: 0;
    box-shadow: ${theme.effects.shadow2};

    --epr-header-padding: 8px;
    --epr-category-label-height: 30px;
    --epr-emoji-size: 22px;
    --epr-emoji-padding: 5px;
    --epr-category-label-text-color: ${theme.colors.secondary800};

    * {
      font-family: inherit;
    }

    .epr-emoji-category-label {
      font-size: 14px;
    }
  }
`;

const EmojiPicker = forwardRef<HTMLDivElement, IProps>(
  (
    {
      children,
      onSelect,
      onOpen,
      onClose,
      placement,
      reactionsDefaultOpen,
      className,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    const colorSchemaSetting = useUserSettingsSelector('colorSchemaSetting');
    const isDarkModeEnabled = colorSchemaSetting === 'DARK';

    const { refs, floatingStyles, context } = useFloating({
      open: isOpen,
      onOpenChange: (open) => {
        setIsOpen(open);
        if (open) {
          onOpen?.();
        } else {
          onClose?.();
        }
      },
      middleware: [offset(5), flip(), shift()],
      whileElementsMounted: autoUpdate,
      strategy: 'fixed',
      placement: placement || 'top',
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([
      click,
      dismiss,
    ]);

    const handleSelectEmoji = (emojiData: EmojiClickData) => {
      onSelect(emojiData);
      setIsOpen(false);

      if (onClose) {
        onClose();
      }
    };

    const referenceElementRef = useMergeRefs([refs.setReference, ref]);

    return (
      <>
        <div
          ref={referenceElementRef}
          {...getReferenceProps()}
          data-open={isOpen}
          className={className}
        >
          {children}
        </div>
        {isOpen && (
          <FloatingLayer>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              <StyledEmojiPickerReact
                width={334}
                height={405}
                onEmojiClick={handleSelectEmoji}
                searchPlaceholder={'Search all emoji'}
                skinTonesDisabled={true}
                theme={isDarkModeEnabled ? Theme.DARK : Theme.LIGHT}
                emojiStyle={EmojiStyle.NATIVE}
                previewConfig={{
                  showPreview: false,
                }}
                reactionsDefaultOpen={reactionsDefaultOpen}
              />
            </div>
          </FloatingLayer>
        )}
      </>
    );
  },
);

export default EmojiPicker;
